import {smartAtom} from "../../util/atom/SmartAtom.ts";
import {ConnectionAtomDep} from "../../common-process/1-metl-connection/MetlConnectionProperties.ts";
import {PromiseRunnerOptionsDefault, type PromiseRunnerOptions} from "../../util/promises/PromiseRunner.tsx";

export interface AnalysisOptionTaskHistory {
    enabled: boolean
    durationToQueryInDays: number
    taskQueryDurationHours: number //How many hours of task history to query per query
    promiseRunner: PromiseRunnerOptions
}

export interface ProjectToAnalyze {
    groupName: string
    projectName: string
    analyzeAllVersions: boolean
    versionsToAnalyze: string[]
}

export interface AnalysisOptionProject {
    analyzeAll: boolean
    analyzeAllSingleVersion?: boolean
    projectsToAnalyze: ProjectToAnalyze[]
    exportConfig?: { // When a version is small, we use the single export endpoint, when it's big we use lots of debug requests
        useExports: boolean
        exportLimit: number // How big should a version be (in jobs) before we switch back to debug
    }
}

export interface AnalysisOptions {
    //A string representation which can be fed in to a query key
    id: string
    analyseSchedules: boolean
    analyseSharedJobs: boolean
    sharedJobRunner: PromiseRunnerOptions
    analyseDrivers: boolean
    analyseTaskHistory: AnalysisOptionTaskHistory
    analyseProjects: AnalysisOptionProject
    jobDownloadStrategy: "INDIVIDUAL" | "EXPORT"
    jobDownloadRunner: PromiseRunnerOptions
    reportAnonymisedErrors: boolean

    analyseGit: boolean
    analyseQueueConfig: boolean
    analyseApiProfiles: boolean
    analyseOauthConnections: boolean
    analyseCredentials: boolean
    analysePasswords: boolean
    analyseWebhooks: boolean
}

const AnalysisOptionsDefault: AnalysisOptions = {
    id: "abc",
    analyseSchedules: true,
    analyseSharedJobs: true,
    sharedJobRunner: PromiseRunnerOptionsDefault,
    analyseDrivers: true,
    analyseGit: true,
    analyseQueueConfig: true,
    analyseApiProfiles: true,
    analyseCredentials: true,
    analysePasswords: true,
    analyseOauthConnections: true,
    analyseWebhooks: true,
    analyseTaskHistory: {
        enabled: true,
        durationToQueryInDays: 30,
        taskQueryDurationHours: 6,
        promiseRunner: PromiseRunnerOptionsDefault
    },
    analyseProjects: {
        analyzeAll: true,
        analyzeAllSingleVersion: true,
        projectsToAnalyze: []
    },
    reportAnonymisedErrors: true,
    jobDownloadStrategy: "INDIVIDUAL",
    jobDownloadRunner: PromiseRunnerOptionsDefault
}

export const [AnalysisOptionsAtom, AnalysisOptionsDep] = smartAtom({
    storageKey: "analysis-options",
    defaultValue: AnalysisOptionsDefault,
    dependencies: [ConnectionAtomDep]
})

export function areOptionsSet(options: AnalysisOptions): boolean {
    const { projectsToAnalyze, analyzeAll } = options.analyseProjects
    if(analyzeAll) return true
    return projectsToAnalyze.length > 0 && projectsToAnalyze.some(s => s.analyzeAllVersions || s.versionsToAnalyze.length > 0)
}